import { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SearchContext } from "../../Context";

/**
 * Strip HTML tags and collapse whitespace for clean plain-text snippets.
 */
function stripHtml(html) {
    if (!html || typeof html !== "string") return html || "";
    return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

/**
 * Build a navigable path for a keyword-search result.
 * Uses "/article/{slug}" — "article" is a dummy categorySlug so the
 * /:categorySlug/:articleSlug route matches. PreviewArticle then
 * auto-redirects to the correct /{realCategorySlug}/{slug} URL.
 * Matches the pattern from the old site (ArticleSearch.js) and BannerSection.js.
 */
function getArticlePath(article) {
    return article.slug ? `/article/${article.slug}` : null;
}

/**
 * Traditional search results list.
 * Figma node 139-2384: "{N} results" header, divider, breadcrumb trail, blue
 * title links (#0c3e9d), description with bold keyword highlighting (#2d2d2d).
 */
export default function SearchResultsList({ results = [], query = "" }) {
    const { setSearchInput, setSearchList } = useContext(SearchContext);
    const { search } = useLocation();

    if (!results.length) return null;

    // Preserve search params (e.g. ?product=gmail) but strip the q= param
    const getSearchSuffix = (path) => {
        if (!search || path === "/") return "";
        const params = new URLSearchParams(search);
        params.delete("q");
        const remaining = params.toString();
        return remaining ? `?${remaining}` : "";
    };

    /** Clear search state so the suggestions dropdown doesn't appear on the target page. */
    const handleResultClick = (e, path) => {
        if (!path) { e.preventDefault(); return; }
        setSearchInput("");
        setSearchList(false);
    };

    return (
        <Box>
            {/* ── Result count header + divider ── */}
            <Box sx={{ px: "4px" }}>
                <Typography
                    sx={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: "20px",
                        color: "#2d2d2d",
                    }}
                >
                    {results.length} results
                </Typography>
            </Box>
            <Divider sx={{ mt: 1, mb: 0, borderColor: "rgba(0,0,0,0.08)" }} />

            {/* ── Result items ── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "32px", mt: "20px" }}>
                {results.map((article) => {
                    const path = getArticlePath(article);
                    const linkTo = path ? `${path}${getSearchSuffix(path)}` : "#";
                    return (
                        <Box key={article.articleId}>
                            {/* Wrap entire result in a React Router <Link> for proper
                                Ctrl+Click / Cmd+Click / middle-click new-tab support */}
                            <Link
                                to={linkTo}
                                style={{ textDecoration: "none", color: "inherit" }}
                                onClick={(e) => handleResultClick(e, path)}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "10px",
                                        cursor: path ? "pointer" : "default",
                                        "&:hover .result-title": path
                                            ? { textDecoration: "underline" }
                                            : {},
                                    }}
                                >
                                    {/* Breadcrumb trail */}
                                    <Box sx={{ px: "4px" }} onClick={(e) => e.stopPropagation()}>
                                        <BreadcrumbTrail
                                            breadcrumb={article.breadcrumb || article.categoryTitle}
                                        />
                                    </Box>

                                    {/* Title — blue link */}
                                    <Box sx={{ px: "4px" }}>
                                        <Typography
                                            className="result-title"
                                            sx={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontWeight: 400,
                                                fontSize: 16,
                                                lineHeight: "24px",
                                                color: "#0c3e9d",
                                            }}
                                        >
                                            {stripHtml(article.title)}
                                        </Typography>
                                    </Box>

                                    {/* Content snippet; 1 line, keyword highlighted */}
                                    <Box sx={{ px: "4px" }}>
                                        <Typography
                                            component="p"
                                            sx={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontWeight: 400,
                                                fontSize: 16,
                                                lineHeight: "24px",
                                                color: "#525252",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                maxWidth: "960px",
                                            }}
                                        >
                                            {highlightQuery(
                                                stripHtml(article.description ?? article.content ?? ""),
                                                query
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Link>
                        </Box>
                    );
                })}
            </Box>
        </Box >
    );
}

/**
 * Breadcrumb trail: Home > CategoryTitle.
 * Category label is non-clickable since the keyword search API doesn't
 * return a categorySlug or fullPath to build a valid link.
 */
function BreadcrumbTrail({ breadcrumb }) {
    if (!breadcrumb) return null;

    const labelStyle = {
        fontFamily: "'Inter', sans-serif",
        fontWeight: 400,
        fontSize: 14,
        lineHeight: "20px",
        color: "#898989",
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Link to="/" onClick={(e) => e.stopPropagation()} style={{ textDecoration: "none" }}>
                <Typography component="span" sx={{ ...labelStyle, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                    Home
                </Typography>
            </Link>
            <ChevronRightIcon sx={{ fontSize: 18, color: "#898989" }} />
            <Typography component="span" sx={labelStyle}>
                {breadcrumb}
            </Typography>
        </Box>
    );
}

/**
 * Highlights query words in text with bold + darker color.
 * Per Figma: bold keywords use font-weight medium and color #2d2d2d.
 */
function highlightQuery(text, query) {
    if (!query || !text) return text;
    const words = query
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 2);
    if (!words.length) return text;

    const regex = new RegExp(`(${words.join("|")})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
        i % 2 === 1 && part ? (
            <Box
                key={i}
                component="span"
                sx={{ fontWeight: 500, color: "#2d2d2d" }}
            >
                {part}
            </Box>
        ) : (
            part
        )
    );
}
